import React, { useMemo, useState } from 'react';
import { useController } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { Popover, Tooltip } from '@mui/material';
import { useTypedSelector } from '@Features/store';
import { fetchAssets } from '@Features/settings/settingsSlice';
import { useSnackbar } from '@Providers/useSnackbar';
import AddAssetDialog from '@Pages/Settings/Assets/AddAssetDialog';
import { upsertAsset } from '@Pages/Settings/Assets/assets.requester';

export const SUBJECT_ICON_PREFIX = 'subject_icon_';

type Props = {
	control: any;
	name: string;
	label?: string;
	disabled?: boolean;
};

/**
 * Compact icon picker for subjects backed by the shared assets store:
 * every asset whose key starts with `subject_icon_` is a library
 * entry. The trigger shows the current icon; the popover grid picks
 * one. Selecting stores the icon URL in the form field, so the saved
 * payload is identical to the old free-upload flow.
 */
const SubjectIconPicker: React.FC<Props> = ({
	control,
	name,
	label,
	disabled,
}) => {
	const dispatch = useDispatch();
	const { displaySnackbar } = useSnackbar();
	const { assets } = useTypedSelector((state) => state.settings);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [addOpen, setAddOpen] = useState(false);

	const {
		field: { value, onChange },
	} = useController({ name, control });

	const icons = useMemo(
		() =>
			Object.entries(assets)
				.filter(([key]) => key.startsWith(SUBJECT_ICON_PREFIX))
				.map(([key, url]) => ({
					key,
					url,
					label: key.slice(SUBJECT_ICON_PREFIX.length).replace(/_/g, ' '),
				}))
				.sort((a, b) => a.label.localeCompare(b.label)),
		[assets]
	);

	const selected = icons.find((icon) => icon.url === value);
	const triggerLabel = selected?.label ?? (value ? 'custom' : 'Select icon');

	const pick = (url: string) => {
		onChange(url);
		setAnchorEl(null);
	};

	const onAddIcon = async (key: string, url: string) => {
		try {
			await upsertAsset(key, url);
			displaySnackbar('success', `Icon "${key}" added to the library`);
			setAddOpen(false);
			dispatch(fetchAssets());
			onChange(url);
		} catch (error: any) {
			const message =
				error?.response?.data?.errors?.[0]?.message ??
				error?.response?.data?.message ??
				'Unable to add icon';
			displaySnackbar('error', message);
		}
	};

	return (
		<Wrapper>
			{label && <PickerLabel>{label}</PickerLabel>}
			<Trigger
				type="button"
				disabled={disabled}
				onClick={(e) => setAnchorEl(e.currentTarget)}
			>
				{value ? (
					<TriggerImage src={value} alt="subject icon" />
				) : (
					<ImageOutlinedIcon fontSize="small" color="disabled" />
				)}
				<TriggerText>{triggerLabel}</TriggerText>
				<ArrowDropDownIcon fontSize="small" color="action" />
			</Trigger>

			<Popover
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			>
				<PopoverContent>
					<TilesGrid>
						{icons.map((icon) => (
							<Tooltip key={icon.key} title={icon.label}>
								<Tile
									$selected={value === icon.url}
									onClick={() => pick(icon.url)}
								>
									<TileImage src={icon.url} alt={icon.label} />
								</Tile>
							</Tooltip>
						))}
						<Tooltip title="Add a new icon to the library">
							<Tile
								$dashed
								onClick={() => {
									setAnchorEl(null);
									setAddOpen(true);
								}}
							>
								<AddIcon color="action" fontSize="small" />
							</Tile>
						</Tooltip>
					</TilesGrid>
					{icons.length === 0 && (
						<EmptyHint>No icons in the library yet — add the first one</EmptyHint>
					)}
				</PopoverContent>
			</Popover>

			<AddAssetDialog
				open={addOpen}
				onClose={() => setAddOpen(false)}
				onSubmit={onAddIcon}
				keyPrefix={SUBJECT_ICON_PREFIX}
				title="Add Icon"
				keyLabel="Icon name"
			/>
		</Wrapper>
	);
};

export default SubjectIconPicker;

const Wrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 1rem;
`;

const PickerLabel = styled.label`
	font-size: 0.9rem;
	color: ${(props) => props.theme.palette.cadet?.main ?? '#6c757d'};
`;

const Trigger = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.375rem 0.5rem 0.375rem 0.75rem;
	background: #fff;
	border: 1px solid ${(props) => props.theme.palette.grey['400']};
	border-radius: 4px;
	cursor: pointer;
	min-width: 10rem;

	:hover:not(:disabled) {
		border-color: ${(props) => props.theme.palette.purple?.main ?? '#6c63ff'};
	}

	:disabled {
		opacity: 0.6;
		cursor: default;
	}
`;

const TriggerImage = styled.img`
	width: 1.5rem;
	height: 1.5rem;
	object-fit: contain;
`;

const TriggerText = styled.span`
	flex: 1;
	text-align: left;
	font-size: 0.85rem;
	color: ${(props) => props.theme.palette.grey['800']};
	text-transform: capitalize;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const PopoverContent = styled.div`
	padding: 0.75rem;
	max-width: 19rem;
`;

const TilesGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(6, 2.5rem);
	gap: 0.375rem;
	max-height: 15rem;
	overflow-y: auto;
`;

const Tile = styled.div<{ $selected?: boolean; $dashed?: boolean }>`
	width: 2.5rem;
	height: 2.5rem;
	border-radius: 6px;
	border: 2px ${(props) => (props.$dashed ? 'dashed' : 'solid')}
		${(props) =>
			props.$selected
				? props.theme.palette.purple?.main ?? '#6c63ff'
				: props.theme.palette.grey['300']};
	background: #fff;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;

	:hover {
		border-color: ${(props) => props.theme.palette.purple?.main ?? '#6c63ff'};
	}
`;

const TileImage = styled.img`
	width: 1.5rem;
	height: 1.5rem;
	object-fit: contain;
`;

const EmptyHint = styled.div`
	font-size: 0.75rem;
	color: ${(props) => props.theme.palette.grey['600']};
	padding: 0.25rem;
`;
